import { Box, Container, CssBaseline, ThemeProvider, createTheme, useMediaQuery, useTheme } from '@mui/material'
import { blue } from '@mui/material/colors'
import { ErrorFallback } from '@src/components/ErrorFallback'
import { AppContext } from '@src/contexts/appContext'
import { getUserToken, isExpiredToken, loadUserFromJwtString } from '@src/data/providers/UserProvider'
import { interceptError } from '@src/services/http-common'
import { AxiosError } from 'axios'
import { forwardRef, useState, useMemo } from 'react'
import { BrowserRouter as Router, Link as RouterLink, LinkProps as RouterLinkProps } from 'react-router-dom'
import LoginPage from '../Home/LoginPage'
import Navigation from './components/Navigation'
import TopLevelRoutes from './Routes'

// eslint-disable-next-line react/display-name
const LinkBehavior = forwardRef<HTMLAnchorElement, Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }>(
  (props, ref) => {
    const { href, ...other } = props
    return <RouterLink ref={ref} to={href} {...other} />
  },
)

const App = () => {
  const [mode, setMode] = useState<'light' | 'dark'>((localStorage.getItem('darkMode') as 'light' | 'dark') || 'dark')
  const [error, setError] = useState<AxiosError | Error | null>(null)
  const jwtToken = getUserToken()
  const initialUser = jwtToken ? loadUserFromJwtString(jwtToken) : undefined
  const [user, setUser] = useState(initialUser && !isExpiredToken(initialUser.exp) ? initialUser : undefined)

  const appStore = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prevMode) => {
          const newMode = prevMode === 'dark' ? 'light' : 'dark'
          localStorage.setItem('darkMode', newMode)
          return newMode
        }),
      user,
      error,
    }),
    [user, error],
  )

  interceptError(setError)

  const theme = useMemo(
    () =>
      createTheme({
        components: {
          MuiLink: { defaultProps: { component: LinkBehavior } },
          MuiButtonBase: { defaultProps: { LinkComponent: LinkBehavior } },
          MuiInputBase: {
            defaultProps: { disableInjectingGlobalStyles: true },
          },
        },
        palette: {
          primary: { main: mode === 'dark' ? blue[200] : blue[700] },
          background: { default: mode === 'dark' ? '#101010' : '#fff' },
          mode,
        },
      }),
    [mode],
  )

  return (
    <ThemeProvider theme={theme}>
      <AppContext.Provider value={appStore}>
        <CssBaseline />
        <Router>
          {!user && <LoginPage setUser={setUser} />}
          {error && <ErrorFallback error={error} resetError={setError} />}
          {user && <AppContent />}
        </Router>
      </AppContext.Provider>
    </ThemeProvider>
  )
}

const AppContent = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const containerStyles = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    flexWrap: 'nowrap',
    height: '100vh',
  }

  const asideStyles = {
    flex: '0 0 auto',
    padding: '1rem',
    width: isMobile ? 'auto' : '15rem',
  }

  const mainStyles = {
    flex: '1 1 auto',
    padding: '1rem',
    overflow: 'auto',
    marginTop: isMobile ? '0rem' : '2.5rem',
  }

  return (
    <Box sx={containerStyles}>
      <Box component="aside" sx={asideStyles}>
        <Navigation />
      </Box>

      <Box component="main" sx={mainStyles}>
        <Container maxWidth="xl" disableGutters>
          <TopLevelRoutes />
        </Container>
      </Box>
    </Box>
  )
}

export default App
